import { AuthContext } from "./context";
import { IAuthContextProviderProps } from "./types";
import { useAuthDerivation } from "./useAuthDerivation";
import { useAuthActions } from "./useAuthActions";
import { useAuthActionsExtra } from "./useAuthActionsExtra";

export function AuthContextProvider({ children }: IAuthContextProviderProps) {
  const {
    authInfoList,
    setAuthInfoList,
    importedAccountAddress,
    setImportedAccount,
    activeSubaccount,
    setActiveSubaccount,
    walletAccount,
    deactivate,
    setLocalPassword,
    showInvalidRefModal,
    setShowInvalidRefModal,
    isGeoblocked,
    storeAuthInfoToLocalStorage,
    getRawAccountAuthInfo,
    decryptedAccountAuthInfo,
    accountApiKeyState,
    accountSigningKeyState,
    getImportedAccountAddress,
  } = useAuthDerivation();

  const {
    encryptAccount,
    unlockAccount,
    enableTrading,
    reconnectImportedAccount,
    manualImportAccount,
    manualSyncAccount,
  } = useAuthActions({
    decryptedAccountAuthInfo,
    authInfoList,
    storeAuthInfoToLocalStorage,
    setAuthInfoList,
    setLocalPassword,
    setImportedAccount,
    getImportedAccountAddress,
  });

  const { disconnectAccount, deleteAuthInfo } = useAuthActionsExtra({
    decryptedAccountAuthInfo,
    authInfoList,
    storeAuthInfoToLocalStorage,
    setAuthInfoList,
    setLocalPassword,
    setImportedAccount,
    importedAccountAddress,
    isGeoblocked,
    deactivate,
  });

  return (
    <AuthContext.Provider
      value={{
        authInfoList,
        isGeoblocked,
        account: decryptedAccountAuthInfo?.account || walletAccount,
        // The master EOA that signs/authorizes (never overridden by a subaccount) — used by the order
        // hooks to resolve the one-click agent while `account` may be a subaccount.
        masterAccount: importedAccountAddress || walletAccount,
        activeSubaccount,
        setActiveSubaccount,
        isAccountImported: !walletAccount,
        getRawAccountAuthInfo,
        getImportedAccountAddress,
        apiKey: decryptedAccountAuthInfo?.encrypted
          ? undefined
          : decryptedAccountAuthInfo?.apiKey,
        apiSecret: decryptedAccountAuthInfo?.encrypted
          ? undefined
          : decryptedAccountAuthInfo?.apiSecret === undefined ||
            decryptedAccountAuthInfo?.apiSecret === ""
          ? decryptedAccountAuthInfo?.apiKey
          : decryptedAccountAuthInfo?.apiSecret,
        signingKey: decryptedAccountAuthInfo?.encrypted
          ? undefined
          : decryptedAccountAuthInfo?.signingKey,
        expiry: decryptedAccountAuthInfo?.encrypted
          ? undefined
          : decryptedAccountAuthInfo?.expiry,
        isEncryptionEnabled: decryptedAccountAuthInfo
          ? decryptedAccountAuthInfo.passwordProtected
          : false,
        accountSigningKeyState,
        accountApiKeyState,
        manualImportAccount,
        manualSyncAccount,
        encryptAccount,
        unlockAccount,
        reconnectImportedAccount,
        disconnectAccount,
        showInvalidRefModal,
        setAuthInfoList,
        enableTrading,
        deleteAuthInfo,
        setShowInvalidRefModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
